import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, Megaphone, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  PageHeader, Spinner, EmptyState, Modal, Field, GoldButton, GhostButton, inputCls, PLATFORM_META,
} from "@/components/app/widgets";

const OBJECTIVES = [
  "Podcast interviews", "YouTube collaborations", "Guest speaking opportunities",
  "Webinars / masterclasses", "Affiliate partnerships", "Audience distribution",
  "Lead generation", "Sales generation", "Product launch", "Strategic partnerships",
];

const CHANNELS = ["podcast", "youtube", "facebook", "tiktok", "instagram"];

export default function Campaigns() {
  const [items, setItems] = useState(null);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    product_id: "", name: "", niche: "", target_audience: "", keywords: "",
    geography: "Worldwide", language: "English", channels: [], objective: "Lead generation",
    targets: { outcome: "", prospects: "", leads: "", customers: "", revenue: "" },
    timeframe: { start: "", end: "" },
  });

  const load = () => api.get("/campaigns").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data.items)).catch(() => {});
  }, []);

  const toggleChannel = (c) =>
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        targets: {
          outcome: form.targets.outcome,
          prospects: Number(form.targets.prospects) || 0,
          leads: Number(form.targets.leads) || 0,
          customers: Number(form.targets.customers) || 0,
          revenue: Number(form.targets.revenue) || 0,
        },
        timeframe: form.timeframe,
      };
      await api.post("/campaigns", payload);
      toast.success("Campaign created — run discovery to fill it with prospects");
      setOpen(false);
      setStep(0);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await api.delete(`/campaigns/${c.campaign_id}`);
      toast.success("Campaign deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!items) return <Spinner label="Loading campaigns" />;

  const stepValid = [
    form.product_id && form.name,
    form.channels.length > 0,
    true,
    true,
  ][step];

  return (
    <div data-testid="campaigns-page">
      <PageHeader title="Distribution Campaigns" sub="One campaign = one focused distribution push.">
        <GoldButton onClick={() => setOpen(true)} data-testid="new-campaign-button"><Plus className="h-4 w-4" /> New Campaign</GoldButton>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No campaigns yet"
          desc="Create a campaign, choose your platforms, and let discovery fill your pipeline."
          action={<GoldButton onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Create Campaign</GoldButton>}
        />
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {items.map((c) => (
            <div key={c.campaign_id} data-testid={`campaign-card-${c.campaign_id}`} className="group border border-[#27272A] bg-[#0A0A0A] p-6 transition-all duration-300 hover:border-[#D4AF37]/50">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/app/campaigns/${c.campaign_id}`} className="font-serif text-xl font-semibold hover:text-[#D4AF37]">
                  {c.name}
                </Link>
                <button onClick={() => remove(c)} data-testid={`delete-campaign-${c.campaign_id}`} className="p-1.5 text-zinc-600 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                {c.product_name} · {c.objective}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {c.channels.map((ch) => (
                  <span key={ch} className={`rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${PLATFORM_META[ch]?.color || "text-zinc-400"}`}>
                    {PLATFORM_META[ch]?.label || ch}
                  </span>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-6 border-t border-white/5 pt-4 text-xs text-zinc-500">
                <span><span className="font-semibold text-white">{c.prospect_count}</span> prospects</span>
                <span><span className="font-semibold text-white">{c.lead_count}</span> leads</span>
                <Link
                  to={`/app/campaigns/${c.campaign_id}`}
                  data-testid={`open-campaign-${c.campaign_id}`}
                  className="ml-auto flex items-center gap-1 text-[#D4AF37] hover:brightness-110"
                >
                  Open <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => { setOpen(false); setStep(0); }} title={`New Distribution Campaign — Step ${step + 1} of 4`} wide>
        <div data-testid="campaign-wizard">
          {step === 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Which product are you promoting?">
                <select
                  data-testid="campaign-product-select"
                  value={form.product_id}
                  onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                  className={inputCls}
                >
                  <option value="">Select a product…</option>
                  {products.map((p) => (
                    <option key={p.product_id} value={p.product_id}>{p.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Campaign name">
                <input
                  type="text" data-testid="campaign-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls} placeholder="e.g. DELF B2 Distribution — Phase A"
                />
              </Field>
              <Field label="Niche">
                <input
                  type="text" data-testid="campaign-niche-input" value={form.niche}
                  onChange={(e) => setForm({ ...form, niche: e.target.value })}
                  className={inputCls} placeholder="e.g. DELF / French exam preparation"
                />
              </Field>
              <Field label="Target audience">
                <input
                  type="text" data-testid="campaign-audience-input" value={form.target_audience}
                  onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                  className={inputCls} placeholder="e.g. DELF B2 candidates"
                />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Keywords (comma separated)">
                  <input
                    type="text" data-testid="campaign-keywords-input" value={form.keywords}
                    onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                    className={inputCls} placeholder="DELF B2, French exam, learn French"
                  />
                </Field>
              </div>
              <Field label="Geography">
                <input
                  type="text" data-testid="campaign-geography-input" value={form.geography}
                  onChange={(e) => setForm({ ...form, geography: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <Field label="Language">
                <input
                  type="text" data-testid="campaign-language-input" value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className={inputCls}
                />
              </Field>
              <div className="sm:col-span-2">
                <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">Channels</span>
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map((c) => (
                    <button
                      key={c} type="button" onClick={() => toggleChannel(c)}
                      data-testid={`channel-${c}`}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        form.channels.includes(c)
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                          : "border-white/15 text-zinc-400 hover:border-white/40"
                      }`}
                    >
                      {PLATFORM_META[c].label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <span className="mb-3 block text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400">Campaign objective</span>
              <div className="grid gap-2 sm:grid-cols-2">
                {OBJECTIVES.map((o) => (
                  <button
                    key={o} type="button" onClick={() => setForm({ ...form, objective: o })}
                    data-testid={`objective-${o.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`border px-4 py-3 text-left text-sm transition-colors ${
                      form.objective === o
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                        : "border-white/10 text-zinc-400 hover:border-white/30"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field label="Target outcome">
                  <input
                    type="text" data-testid="campaign-target-outcome-input" value={form.targets.outcome}
                    onChange={(e) => setForm({ ...form, targets: { ...form.targets, outcome: e.target.value } })}
                    className={inputCls} placeholder="e.g. Book 5 podcast interviews and generate qualified leads"
                  />
                </Field>
              </div>
              {[["prospects", "Target prospects"], ["leads", "Target leads"], ["customers", "Target customers"], ["revenue", "Target revenue ($)"]].map(([k, label]) => (
                <Field key={k} label={label}>
                  <input
                    type="number" min="0" data-testid={`campaign-target-${k}-input`} value={form.targets[k]}
                    onChange={(e) => setForm({ ...form, targets: { ...form.targets, [k]: e.target.value } })}
                    className={inputCls} placeholder="0"
                  />
                </Field>
              ))}
              <Field label="Start date">
                <input
                  type="date" data-testid="campaign-start-input" value={form.timeframe.start}
                  onChange={(e) => setForm({ ...form, timeframe: { ...form.timeframe, start: e.target.value } })}
                  className={inputCls}
                />
              </Field>
              <Field label="End date">
                <input
                  type="date" data-testid="campaign-end-input" value={form.timeframe.end}
                  onChange={(e) => setForm({ ...form, timeframe: { ...form.timeframe, end: e.target.value } })}
                  className={inputCls}
                />
              </Field>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <GhostButton type="button" onClick={() => (step === 0 ? setOpen(false) : setStep(step - 1))} data-testid="wizard-back-button">
              <ChevronLeft className="h-4 w-4" /> {step === 0 ? "Cancel" : "Back"}
            </GhostButton>
            {step < 3 ? (
              <GoldButton type="button" disabled={!stepValid} onClick={() => setStep(step + 1)} data-testid="wizard-next-button">
                Next <ChevronRight className="h-4 w-4" />
              </GoldButton>
            ) : (
              <GoldButton type="button" disabled={saving || !form.product_id || !form.name} onClick={save} data-testid="wizard-create-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Campaign"}
              </GoldButton>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
